import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  RepeatWrapping,
  LinearFilter,
  LinearMipmapLinearFilter,
  SRGBColorSpace,
  Texture,
  CanvasTexture,
} from 'three';
import { BAKED_LIGHT_ATTR } from './bake-kernel';
import { EMIT, SURFACE } from './palette';

/**
 * Every material in the game comes from here. Two reasons: the reference sheet's
 * cohesion depends on a small closed set of surfaces, and sharing material
 * instances lets three batch aggressively across a mall-sized scene.
 */

/**
 * The only source of randomness allowed in a texture. `Math.random` is banned
 * here, and this is the reason.
 *
 * Three generators used the real one: the checker floor's grime specks, the
 * atrium tile's per-cell value jitter, and the sky's clouds and stars. The tile
 * one was the expensive mistake — a 2x2 cell grid means the whole atrium floor
 * repeats four random values, so its average brightness moved by a few percent
 * on every page load. That is invisible in isolation and ruinous to the QA
 * harness: two capture runs of the same commit came back with the jewellery
 * store's black point anywhere between 12% and 21%, which reads exactly like a
 * regression and cost most of a night to prove was not one.
 *
 * A capture harness is only worth having if a difference between two runs means
 * a difference in the code. Everything decorative gets a fixed seed.
 */
export function seeded(seed: number): () => number {
  let s = (seed * 9301 + 49297) % 233280;
  return () => (s = (s * 9301 + 49297) % 233280) / 233280;
}

const cache = new Map<string, MeshStandardMaterial | MeshBasicMaterial>();

function memo<T extends MeshStandardMaterial | MeshBasicMaterial>(key: string, make: () => T): T {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const made = make();
  cache.set(key, made);
  return made;
}

/**
 * The name of the geometry attribute that carries baked shop lighting.
 *
 * Declared in `bake-kernel.ts` — which is the half of the lightmapper that runs
 * inside a Web Worker, and so cannot import this file, which builds canvases.
 * Re-exported here because this is where the shader that reads it lives.
 *
 * See `bakeStaticLight`. Every lit surface material reads it; geometry that has
 * not been baked simply lacks it, and WebGL supplies zero for a missing
 * attribute, which here means "no shop light falls here" — the safe default.
 * (Contrast the `color` attribute, where a missing value means BLACK and eats
 * the mesh. Additive channels are forgiving in a way multiplicative ones are
 * not, and that is why this is a separate attribute rather than folded in.)
 */
export { BAKED_LIGHT_ATTR };

/**
 * Teach a standard material to add baked irradiance to what it renders.
 *
 * Three.js has no vertex-emissive channel, so this splices one in. It goes into
 * `totalEmissiveRadiance` rather than into the light loop because by the time
 * that chunk runs, `diffuseColor` is already albedo times vertex colour in
 * linear space — so `bakedLight * diffuseColor` is exactly the diffuse response
 * of the surface to the light, which is the whole definition of a lightmap.
 *
 * The alternative was leaving the shop lights as real point lights. Measured on
 * this map, forty-seven of them ran at three frames a second and even eight cost
 * more than half the frame; a forward renderer evaluates every light for every
 * lit fragment on screen whether or not the light is anywhere near it. The mall
 * is a fixed map lit by fixtures bolted to its ceilings — none of that has any
 * business being recomputed sixty times a second.
 */
export function receiveBakedLight<T extends MeshStandardMaterial>(m: T): T {
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>\nattribute vec3 ${BAKED_LIGHT_ATTR};\nvarying vec3 vBakedLight;`,
      )
      .replace('#include <begin_vertex>', `#include <begin_vertex>\nvBakedLight = ${BAKED_LIGHT_ATTR};`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBakedLight;')
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vBakedLight * diffuseColor.rgb;',
      );
  };
  // Two materials that compile to different programs must not share a cache
  // entry. Without this three.js would hand the unpatched program to the patched
  // material, and the bake would silently render as nothing at all.
  m.customProgramCacheKey = () => 'baked';
  return m;
}

/**
 * Flat-shaded standard surface. `flatShading` is what gives the low-poly read —
 * without it, low-poly geometry looks like bad high-poly geometry.
 */
export function surface(
  key: string,
  color: Color,
  opts: { roughness?: number; metalness?: number; map?: Texture | null; flat?: boolean } = {},
): MeshStandardMaterial {
  return memo(`surf:${key}`, () => {
    const m = new MeshStandardMaterial({
      color: color.clone(),
      roughness: opts.roughness ?? 0.92,
      metalness: opts.metalness ?? 0.0,
      flatShading: opts.flat ?? true,
      // Vertex colours carry our baked contact-shadow darkening.
      vertexColors: true,
    });
    if (opts.map) m.map = opts.map;
    return receiveBakedLight(m);
  });
}

/**
 * Unlit emitter. Never receives light, always blooms. Used for signs, tubes, screens.
 *
 * The intensity is part of the cache key, and that is not a detail. It used to
 * key on the name alone, so the FIRST caller to ask for a given name silently
 * decided the brightness for every later one — which is why dimming a store's
 * ceiling strips did nothing at all: unit one had already created `fluoroPanel`
 * at full brightness and every other unit in the mall was handed that same
 * material back. Any parameter that changes what the material looks like has to
 * be in the key, or the memo is a bug rather than an optimisation.
 */
/**
 * One global multiplier on every emitter, because AgX needs light sources driven
 * far harder than the numbers at the call sites suggest.
 *
 * Measured, not guessed: with this at 1.0 the brightest pixel anywhere in the
 * mall was 232/255 and less than 0.15% of any frame cleared 200. Nothing clipped
 * — not the neon, not the fluorescents, not the moon. A night scene reads as lit
 * because the sources themselves are blown white with bloom around them, and
 * without that the whole building looks like an underexposed photograph of a lit
 * building rather than a dark building with lights in it.
 *
 * The reason it is one knob rather than forty edited call sites: the relative
 * intensities are already right — a big ceiling panel asks for ~0.6, a neon
 * letterform asks for ~1.6 — and that relationship is what stops large emitters
 * going flat. Scaling them together preserves it. Keep new call sites in those
 * same units and let this do the conversion to display brightness.
 */
const EMIT_GAIN = 2.4;

export function emitter(key: string, color: Color, intensity = 1): MeshBasicMaterial {
  return memo(`emit:${key}:${intensity.toFixed(3)}`, () => {
    const c = color.clone().multiplyScalar(intensity * EMIT_GAIN);
    return new MeshBasicMaterial({ color: c, toneMapped: true, fog: true });
  });
}

/** Emissive surface that ALSO takes light — shopfront glass with product behind it. */
export function glow(key: string, base: Color, emissive: Color, strength = 1.4) {
  return memo(`glow:${key}:${strength.toFixed(3)}`, () =>
    receiveBakedLight(
      new MeshStandardMaterial({
        color: base.clone(),
        emissive: emissive.clone(),
        emissiveIntensity: strength,
        roughness: 0.5,
        metalness: 0.1,
        flatShading: true,
        vertexColors: true,
      }),
    ),
  );
}

export const MATERIALS = {
  concrete: () => surface('concrete', SURFACE.concrete, { roughness: 0.96 }),
  concreteDark: () => surface('concreteDark', SURFACE.concreteDark, { roughness: 0.97 }),
  tile: () => surface('tile', SURFACE.tileLight, { roughness: 0.42, metalness: 0.04 }),
  tileDark: () => surface('tileDark', SURFACE.tileDark, { roughness: 0.45, metalness: 0.04 }),
  plaster: () => surface('plaster', SURFACE.plaster, { roughness: 0.94 }),
  plasterDim: () => surface('plasterDim', SURFACE.plasterDim, { roughness: 0.95 }),
  /**
   * Display velvet — case decks, jewellery pads, anything a small bright object
   * has to be legible against. Deliberately far darker than `plasterDim`, which
   * is a mid grey and lifts to near-white the moment a case light hits it; a
   * gold speck on that is invisible, and the case reads as an empty lightbox.
   */
  velvet: () => surface('velvet', SURFACE.velvet, { roughness: 0.98 }),
  cardboard: () => surface('cardboard', SURFACE.cardboard, { roughness: 0.95 }),
  /**
   * The one material every piece of product in the mall shares — cereal boxes,
   * pill packets, coats, shoes. Its colour comes from the geometry, via
   * `tintVertexColor`, so thousands of differently coloured objects can still
   * batch into a handful of draws.
   *
   * White on purpose, and this is the whole point of it. A standard material
   * multiplies its own colour by the vertex colour, so tinting against
   * `plaster` — a mid grey — would land every product at a bit over half the
   * brightness it was authored at, which is not a tint, it is a dimmer.
   */
  card: () => surface('card', new Color(0xffffff), { roughness: 0.9 }),
  /**
   * Tintable gloss — bottle glass, phone screens, cling film, scope lenses.
   *
   * Deliberately opaque. The real `glass` material is transparent with
   * `depthWrite` off, which is right for a shopfront pane four metres wide and
   * wrong for a perfume bottle two centimetres across: at that size the sort
   * order flips every frame and the object flickers inside itself. This just
   * takes a hard specular off the key light, which is all a small glass thing
   * needs to read as glass.
   */
  gloss: () => surface('gloss', new Color(0xffffff), { roughness: 0.14, metalness: 0.08 }),
  steel: () => surface('steel', SURFACE.steel, { roughness: 0.44, metalness: 0.72 }),
  steelDark: () => surface('steelDark', SURFACE.steelDark, { roughness: 0.5, metalness: 0.68 }),
  rubber: () => surface('rubber', SURFACE.rubber, { roughness: 0.98 }),
  timber: () => surface('timber', SURFACE.timber, { roughness: 0.88 }),
  foliage: () => surface('foliage', SURFACE.foliage, { roughness: 0.9 }),
  mannequin: () => surface('mannequin', SURFACE.mannequin, { roughness: 0.78 }),
  /**
   * Painted structural steel — canopy ribs and spars only. Matte and barely
   * metallic on purpose: a polished rib forty metres long catches the moon along
   * its entire edge and renders as a blown-white streak across the night sky,
   * which reads as a scratch on the lens rather than a building.
   */
  steelPainted: () => surface('steelPainted', SURFACE.steelDark, { roughness: 0.85, metalness: 0.1 }),

  /**
   * Shopfront glazing. Reflective enough to catch sign light, clear enough to
   * shop through — you have to be able to see the loot before you break in, and
   * at opacity 0.28 with metalness 0.35 the panes went milky grey and hid the
   * store interiors entirely, which defeats the whole point of a glass frontage.
   */
  glass: () =>
    memo('glass', () => {
      const m = new MeshStandardMaterial({
        color: SURFACE.glass.clone(),
        roughness: 0.1,
        metalness: 0.18,
        transparent: true,
        opacity: 0.15,
        side: DoubleSide,
        depthWrite: false,
        flatShading: false,
      });
      return m;
    }),

  /**
   * Overhead glazing. Nearly clear, and deliberately non-metallic: a metalness
   * of 0.35 turns a roof pane into a mirror that catches the moon and reads as
   * a solid white lid, which is exactly the opposite of what a skylight is for.
   * The structure you see against the sky should be the ribs, not the glass.
   */
  canopyGlass: () =>
    memo('canopyGlass', () => {
      const m = new MeshStandardMaterial({
        color: SURFACE.glass.clone(),
        roughness: 0.03,
        metalness: 0.0,
        transparent: true,
        opacity: 0.1,
        side: DoubleSide,
        depthWrite: false,
        flatShading: false,
      });
      return m;
    }),

  /** Balustrade infill. Same reasoning as the canopy — a handrail should not
   *  out-shine the shopfronts behind it. */
  balustrade: () =>
    memo('balustrade', () => {
      const m = new MeshStandardMaterial({
        color: SURFACE.glass.clone(),
        roughness: 0.08,
        metalness: 0.08,
        transparent: true,
        opacity: 0.14,
        side: DoubleSide,
        depthWrite: false,
        flatShading: false,
      });
      return m;
    }),

  /**
   * Galvanised chain-link, as an alpha-cut plane rather than woven wire.
   *
   * Alpha-TESTED, not alpha-blended, and that is the whole design. A blended
   * fence writes no depth, so it sorts against the car park behind it every
   * frame and swims; a cut-out fence is an ordinary opaque surface with holes in
   * it, which is what a fence physically is. It also means the bake can trace
   * through it — mesh over a lit apron has to read as mesh, not as a grey film.
   */
  chainLink: () =>
    memo('chainLink', () => {
      const m = new MeshStandardMaterial({
        // Galvanising is grey, not white. At full albedo the near fence came out
        // brighter than the building behind it and pulled the eye to the edge of
        // the map, which is the last place it should be going.
        color: new Color(0xa8b0ba),
        map: makeChainLinkTexture(),
        alphaTest: 0.35,
        roughness: 0.6,
        metalness: 0.35,
        side: DoubleSide,
        flatShading: false,
        vertexColors: true,
      });
      return receiveBakedLight(m);
    }),

  fluoroTube: () => emitter('fluoro', EMIT.fluoro, 1.9),
  sodiumLamp: () => emitter('sodium', EMIT.sodium, 2.2),
  shopSpill: () => emitter('shopSpill', EMIT.shopWarm, 1.3),
  exitSign: () => emitter('exit', EMIT.exitGreen, 1.6),
  crtScreen: () => emitter('crt', EMIT.crt, 1.1),
} as const;

// ---------------------------------------------------------------------------
// Signage
// ---------------------------------------------------------------------------

/**
 * Deadpan corporate signage, drawn to a canvas at runtime.
 *
 * The reference sheet's humour lives entirely in typography: condensed uppercase
 * sans, wide tracking, matter-of-fact wording ("DEPARTMENT OF THINGS", "BUY MORE
 * FOR NO REASON"). Generating these procedurally means adding a store is a
 * one-line data change, not an art task.
 */
export interface SignOptions {
  text: string;
  color?: Color;
  /** Pixels of canvas per world metre. 128 keeps text crisp at melee range. */
  pixelsPerMetre?: number;
  widthMetres: number;
  heightMetres: number;
  font?: 'condensed' | 'wide' | 'handwritten';
  align?: 'center' | 'left';
  /** Draw a thin box around the text — reads as a lightbox fascia. */
  boxed?: boolean;
  /** 0 = crisp, 1 = failing tube. Adds uneven alpha across the glyphs. */
  flicker?: number;
}

const FONT_STACKS: Record<NonNullable<SignOptions['font']>, string> = {
  condensed: '"Haettenschweiler", "Arial Narrow", "Oswald", Impact, sans-serif',
  wide: '"Futura", "Century Gothic", "Avenir Next", sans-serif',
  handwritten: '"Bradley Hand", "Segoe Script", cursive',
};

export function makeSignTexture(opts: SignOptions): CanvasTexture {
  const ppm = opts.pixelsPerMetre ?? 128;
  const w = Math.max(8, Math.round(opts.widthMetres * ppm));
  const h = Math.max(8, Math.round(opts.heightMetres * ppm));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  ctx.clearRect(0, 0, w, h);

  const color = (opts.color ?? EMIT.signAmber).clone();
  const css = `rgb(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)})`;

  const lines = opts.text.split('\n');
  const pad = h * 0.14;
  const lineH = (h - pad * 2) / lines.length;
  let fontPx = Math.floor(lineH * 0.82);

  const family = FONT_STACKS[opts.font ?? 'condensed'];
  // Shrink until the widest line fits with tracking applied.
  const tracking = 0.09;
  const fits = (px: number) => {
    ctx.font = `600 ${px}px ${family}`;
    return lines.every((l) => ctx.measureText(l).width * (1 + tracking) <= w - pad * 2);
  };
  while (fontPx > 6 && !fits(fontPx)) fontPx -= 2;
  ctx.font = `600 ${fontPx}px ${family}`;

  ctx.textBaseline = 'middle';
  ctx.textAlign = opts.align === 'left' ? 'left' : 'center';
  ctx.fillStyle = css;
  // A soft halo sells the "this is a light source" read before bloom even runs.
  //
  // Capped in WORLD units, not glyph units. Scaling the halo with the font seems
  // right until you build a fifteen-metre façade sign: the glyphs are 1.5m tall,
  // the halo becomes half a metre of spill, and the counters of the letters fill
  // in so the whole thing renders as one glowing slab with no legible name on it.
  // Real neon bleeds a fixed few centimetres into the air whatever size the tube
  // is bent to, and bloom then adds its own screen-space spread on top.
  ctx.shadowColor = css;
  ctx.shadowBlur = Math.min(fontPx * 0.35, ppm * 0.06);

  lines.forEach((line, i) => {
    const y = pad + lineH * (i + 0.5);
    const x = opts.align === 'left' ? pad : w / 2;
    drawTracked(ctx, line, x, y, fontPx * tracking, opts.align === 'left' ? 'left' : 'center');
  });

  if (opts.boxed) {
    ctx.shadowBlur = fontPx * 0.2;
    ctx.strokeStyle = css;
    ctx.lineWidth = Math.max(2, h * 0.02);
    ctx.strokeRect(pad * 0.4, pad * 0.4, w - pad * 0.8, h - pad * 0.8);
  }

  if (opts.flicker && opts.flicker > 0) {
    // Punch irregular holes in the alpha so the tube reads as dying.
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    for (let i = 3; i < d.length; i += 4) {
      const px = (i - 3) / 4;
      const x = px % w;
      const bandNoise = Math.sin(x * 0.07) * 0.5 + 0.5;
      if (bandNoise < opts.flicker * 0.55) d[i] = Math.floor(d[i] * (1 - opts.flicker * 0.8));
    }
    ctx.putImageData(img, 0, 0);
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Canvas2D has no letter-spacing in older engines; draw glyph by glyph. */
function drawTracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  spacing: number,
  align: 'left' | 'center',
) {
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, text.length - 1);
  let cursor = align === 'center' ? x - total / 2 : x;
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  [...text].forEach((ch, i) => {
    ctx.fillText(ch, cursor, y);
    cursor += widths[i] + spacing;
  });
  ctx.textAlign = prevAlign;
}

/** Emissive sign material from generated text. Not memoised — each sign is unique. */
export function signMaterial(opts: SignOptions): MeshBasicMaterial {
  const tex = makeSignTexture(opts);
  return new MeshBasicMaterial({
    map: tex,
    transparent: true,
    toneMapped: true,
    fog: true,
    depthWrite: false,
  });
}

// ---------------------------------------------------------------------------
// Tiling textures, generated procedurally (no binary assets to ship)
// ---------------------------------------------------------------------------

/** Checkerboard floor for the service corridors (reference panel 4). */
/**
 * Tarmac, as aggregate rather than as a colour.
 *
 * The forecourt was a single untextured plane thirty-four metres deep, and an
 * untextured plane is the most reliable tell there is that a scene was built
 * rather than photographed: nothing in the near field resolves as you walk onto
 * it, so the ground reads as a backdrop the player is sliding over. Real tarmac
 * is a lot of small pale stones in a dark binder, and at grazing angles those
 * stones are the only thing carrying the surface.
 *
 * Deliberately low contrast. This is multiplied by an albedo that is already
 * dark and then lit almost entirely by a lightmap, so anything with real bite
 * in it turns into visible tiling the moment the player looks along the apron.
 */
export function makeTarmacTexture(repeat = 10): CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);

  const r = seeded(90210);
  // Aggregate. Two passes so the stones sit at two scales instead of reading as
  // uniform noise, which is what makes procedural grit look like film grain.
  for (const [count, max, alpha] of [
    [5200, 1.7, 0.1],
    [1300, 3.4, 0.07],
  ] as const) {
    for (let i = 0; i < count; i++) {
      ctx.fillStyle = r() > 0.5 ? `rgba(255,255,255,${alpha})` : `rgba(0,0,0,${alpha * 1.6})`;
      ctx.beginPath();
      ctx.arc(r() * size, r() * size, 0.5 + r() * max, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Patch repairs and the long dark seams between laying passes. A car park is
  // resurfaced in strips and the joints never match.
  ctx.strokeStyle = 'rgba(0,0,0,0.10)';
  ctx.lineWidth = 2.2;
  for (let i = 0; i < 5; i++) {
    ctx.beginPath();
    ctx.moveTo(r() * size, r() * size);
    for (let k = 0; k < 4; k++) ctx.lineTo(r() * size, r() * size);
    ctx.stroke();
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.anisotropy = 8;
  return tex;
}

/** One diamond tile of chain-link at a given size and wire weight. */
function chainLinkTile(size: number, lineWidth: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'square';
  // Two diagonals per tile, drawn three times each so the strokes wrap cleanly
  // across the tile seam instead of stopping short of it.
  for (const [dx, dy] of [[1, 1], [1, -1]] as const) {
    ctx.strokeStyle = dy > 0 ? 'rgba(196,204,212,1)' : 'rgba(150,158,168,1)';
    for (const off of [-size, 0, size]) {
      ctx.beginPath();
      ctx.moveTo(off, dy > 0 ? 0 : size);
      ctx.lineTo(off + size * dx, dy > 0 ? size : 0);
      ctx.stroke();
    }
  }
  return canvas;
}

/**
 * Chain-link diamond mesh, transparent between the wires.
 *
 * One tile is one diamond, drawn as two crossing strokes on a transparent
 * canvas — and then drawn again, by hand, once per mip level, which is the only
 * part of this worth reading.
 *
 * An alpha-tested texture cannot use an averaged mip chain. A tile that is a
 * fifth wire and four fifths hole averages to an alpha of about 0.2, so two
 * levels down every texel is under any threshold worth having and the fence
 * stops drawing — which is exactly what happened: the first build rendered the
 * posts and the rails and nothing in between. Turning mipmaps off brought the
 * wire back and replaced the problem with the other one, a fence sampled at a
 * tenth of its Nyquist rate, which crawls with coloured speckle at any distance.
 *
 * Both failures come from the same place: shrinking the tile is allowed to
 * shrink the wire. So each level is redrawn rather than resampled, with the
 * stroke widening as a fraction of the tile as the tile gets smaller. Coverage
 * climbs from a fifth at full size to about half at the bottom, so the alpha
 * test always has something to bite on, and the levels are real averages of a
 * real image so trilinear has something to blend. Near, it is wire; far, it
 * closes into the grey haze a fence actually becomes at seventy metres.
 */
export function makeChainLinkTexture(repeat = 1): CanvasTexture {
  const size = 64;
  const tex = new CanvasTexture(chainLinkTile(size, 5));
  const mips: HTMLCanvasElement[] = [];
  for (let level = 0, s = size; s >= 1; level++, s >>= 1) {
    // Proportional weight, then a boost that reaches ~3x at the bottom of the
    // chain. The floor of 0.9px is what stops the last two levels vanishing.
    const w = Math.max(0.9, ((5 * s) / size) * (1 + (2.1 * level) / 6));
    mips.push(chainLinkTile(s, w));
  }
  tex.mipmaps = mips as unknown as CanvasTexture['mipmaps'];
  tex.generateMipmaps = false;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.anisotropy = 8;
  return tex;
}

export function makeCheckerTexture(repeat = 8): CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const a = SURFACE.checkerPale;
  const b = SURFACE.checkerWarm;
  const toCss = (c: Color) =>
    `rgb(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)})`;
  const cell = size / 2;
  for (let y = 0; y < 2; y++) {
    for (let x = 0; x < 2; x++) {
      ctx.fillStyle = toCss((x + y) % 2 === 0 ? a : b);
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  }
  // Grime: a few thousand dark specks so the floor is not a perfect grid.
  ctx.fillStyle = 'rgba(0,0,0,0.05)';
  const grime = seeded(4021);
  for (let i = 0; i < 2400; i++) {
    ctx.fillRect(grime() * size, grime() * size, 1, 1);
  }
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.anisotropy = 8;
  return tex;
}

/**
 * A single security feed, drawn as a picture rather than a colour.
 *
 * Fifteen identical green rectangles read as a texture swatch; fifteen different
 * dim rooms read as fifteen places a thief could be right now, which is the
 * entire dramatic point of the control room. Everything is derived from `seed`
 * so the wall is stable across frames and across screenshot runs.
 */
/**
 * Retail display loop for the electronics-store television wall.
 *
 * Deliberately NOT the same generator as the security feeds: a shop TV shows a
 * demo reel, not a camera, and rendering both from one function made the wall of
 * televisions read as sixteen more security monitors. Three modes cycle by seed
 * so the wall has variety without needing sixteen textures authored by hand.
 */
export function makeTvTexture(seed: number): CanvasTexture {
  const W = 128;
  const H = 80;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  let s = seed * 7919 + 13;
  const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);

  const mode = seed % 3;
  if (mode === 0) {
    // Colour bars. Muted well below broadcast saturation — this is the only
    // multi-hue surface in the mall and at full chroma it fights every sign.
    const BARS = ['#8f9298', '#8f8f5a', '#5a8f8f', '#5a8f5a', '#8f5a8f', '#8f5a5a', '#5a5a8f'];
    BARS.forEach((c, i) => {
      ctx.fillStyle = c;
      ctx.fillRect((i * W) / BARS.length, 0, W / BARS.length + 1, H * 0.78);
    });
    ctx.fillStyle = '#1a1c20';
    ctx.fillRect(0, H * 0.78, W, H * 0.22);
    ctx.fillStyle = '#c9d6e0';
    ctx.font = '9px sans-serif';
    ctx.fillText('DEMO LOOP', 6, H - 6);
  } else if (mode === 1) {
    // A product beauty shot, abstracted to a lit object on a gradient. At this
    // resolution nobody reads it as anything but "an advert", which is all it
    // has to be from four metres behind a pane of glass.
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, '#2a3a4a');
    grad.addColorStop(1, '#101820');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#d8e4ee';
    ctx.beginPath();
    ctx.ellipse(W / 2, H * 0.55, 22, 16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#8fb4d0';
    ctx.fillRect(W / 2 - 30, H * 0.82, 60, 3);
  } else {
    // No signal. Every wall of televisions has one.
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x += 2) {
        const v = Math.floor(rnd() * 90) + 20;
        ctx.fillStyle = `rgb(${v},${v + 3},${v + 6})`;
        ctx.fillRect(x, y, 2, 1);
      }
    }
  }

  // Scanlines over everything. They are what make a flat canvas read as a screen.
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  for (let y = 0; y < H; y += 2) ctx.fillRect(0, y, W, 1);

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

export function makeCrtTexture(seed: number, label: string): CanvasTexture {
  const W = 160;
  const H = 120;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // Deterministic pseudo-random. No Math.random: screenshots must be comparable.
  let s = seed * 9301 + 49297;
  const rnd = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };

  const dead = seed % 7 === 3;
  if (dead) {
    // A failed feed. Every bank of monitors has one and it is the only thing on
    // the wall a thief can rely on.
    ctx.fillStyle = 'rgb(16,20,18)';
    ctx.fillRect(0, 0, W, H);
    for (let i = 0; i < 2600; i++) {
      const v = Math.round(rnd() * 90);
      ctx.fillStyle = `rgb(${v},${v + 12},${v + 4})`;
      ctx.fillRect(rnd() * W, rnd() * H, 2, 1);
    }
  } else {
    // A room: floor, horizon, and a few silhouettes standing in it.
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, 'rgb(10,26,20)');
    grad.addColorStop(0.52, 'rgb(22,54,40)');
    grad.addColorStop(1, 'rgb(38,86,64)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    const horizon = 44 + rnd() * 22;
    ctx.fillStyle = 'rgba(120,200,164,0.35)';
    ctx.fillRect(0, horizon, W, 1);

    // Perspective floor lines, converging on an off-centre vanishing point.
    const vp = W * (0.25 + rnd() * 0.5);
    ctx.strokeStyle = 'rgba(120,200,164,0.16)';
    for (let i = -3; i <= 3; i++) {
      ctx.beginPath();
      ctx.moveTo(vp, horizon);
      ctx.lineTo(vp + i * W * 0.42, H);
      ctx.stroke();
    }

    const blocks = 2 + Math.floor(rnd() * 3);
    for (let i = 0; i < blocks; i++) {
      const bw = 8 + rnd() * 26;
      const bh = 14 + rnd() * 40;
      const bx = rnd() * (W - bw);
      ctx.fillStyle = `rgba(6,20,14,${(0.45 + rnd() * 0.4).toFixed(2)})`;
      ctx.fillRect(bx, horizon - bh + rnd() * 18, bw, bh);
    }
  }

  // Scanlines and a vignette. This is what makes it a CRT and not a photograph.
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  for (let y = 0; y < H; y += 2) ctx.fillRect(0, y, W, 1);
  const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.85);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  ctx.font = '9px "Courier New", monospace';
  ctx.fillStyle = 'rgba(190,255,220,0.8)';
  ctx.fillText(label, 5, 12);
  if (!dead) ctx.fillText('REC', W - 26, H - 6);

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Large-format polished mall tile with visible grout — the atrium floor. */
export function makeMallTileTexture(repeat = 12): CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#9a978f';
  ctx.fillRect(0, 0, size, size);
  // Subtle per-tile value variation so the floor is not flat under a light pool.
  const cells = 2;
  const cs = size / cells;
  const jitter = seeded(1877);
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      const v = 0.94 + jitter() * 0.12;
      ctx.fillStyle = `rgba(255,255,255,${(v - 1) * 0.5 + 0.5})`;
      ctx.globalCompositeOperation = 'overlay';
      ctx.fillRect(x * cs, y * cs, cs, cs);
      ctx.globalCompositeOperation = 'source-over';
    }
  }
  ctx.strokeStyle = 'rgba(40,40,44,0.55)';
  ctx.lineWidth = 3;
  for (let i = 0; i <= cells; i++) {
    ctx.beginPath();
    ctx.moveTo(i * cs, 0);
    ctx.lineTo(i * cs, size);
    ctx.moveTo(0, i * cs);
    ctx.lineTo(size, i * cs);
    ctx.stroke();
  }
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.anisotropy = 8;
  return tex;
}

// ---------------------------------------------------------------------------
// Vertex-colour contact shading
// ---------------------------------------------------------------------------

/**
 * Fakes ambient occlusion by darkening vertices near the floor and near inside
 * corners. This is the single cheapest thing that stops flat-shaded boxes from
 * looking like untextured programmer geometry, and it is why every material in
 * this file enables `vertexColors`.
 */
export function applyContactShading(
  geometry: BufferGeometry,
  opts: { floorY?: number; falloff?: number; strength?: number; base?: number } = {},
): BufferGeometry {
  const floorY = opts.floorY ?? 0;
  const falloff = opts.falloff ?? 1.2;
  const strength = opts.strength ?? 0.45;
  const base = opts.base ?? 1;

  const pos = geometry.getAttribute('position');
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const t = Math.min(1, Math.max(0, (y - floorY) / falloff));
    const shade = base * (1 - strength * (1 - t));
    colors[i * 3] = shade;
    colors[i * 3 + 1] = shade;
    colors[i * 3 + 2] = shade;
  }
  geometry.setAttribute('color', new BufferAttribute(colors, 3));
  return geometry;
}

/** Uniform vertex colour, for geometry that needs the attribute but no gradient. */
export function flatVertexColor(geometry: BufferGeometry, value = 1): BufferGeometry {
  const pos = geometry.getAttribute('position');
  const colors = new Float32Array(pos.count * 3).fill(value);
  geometry.setAttribute('color', new BufferAttribute(colors, 3));
  return geometry;
}

/**
 * Paint a whole geometry one colour in its vertex attribute.
 *
 * The alternative — and what this replaced — is cloning a material per object
 * and setting `material.color`. Visually identical, and fine until you count:
 * the supermarket alone was four hundred and forty meshes carrying three hundred
 * and thirty-three separate materials, one per box of cereal. Distinct materials
 * cannot be batched, so every one of those was a guaranteed draw call forever.
 * Carrying the colour in the geometry instead means every packet, coat and shoe
 * in the mall shares a single material and the whole store collapses to a
 * handful of draws.
 *
 * Multiplies rather than overwrites, so it composes with `applyContactShading` —
 * bake the shading first, then tint, and the object keeps its contact darkening.
 */
export function tintVertexColor(geometry: BufferGeometry, hexColor: number): BufferGeometry {
  const c = new Color().setHex(hexColor, 'srgb');
  const pos = geometry.getAttribute('position');
  let attr = geometry.getAttribute('color') as BufferAttribute | undefined;
  if (!attr) {
    attr = new BufferAttribute(new Float32Array(pos.count * 3).fill(1), 3);
    geometry.setAttribute('color', attr);
  }
  for (let i = 0; i < attr.count; i++) {
    attr.setXYZ(i, attr.getX(i) * c.r, attr.getY(i) * c.g, attr.getZ(i) * c.b);
  }
  attr.needsUpdate = true;
  return geometry;
}

/**
 * Safety net: give every geometry in a subtree a neutral colour attribute if it
 * does not already have one.
 *
 * `surface()` turns on `vertexColors` for all standard materials, because that
 * is how baked contact shading rides along. The consequence is a trap with no
 * warning attached: WebGL substitutes (0,0,0) for a missing attribute, so any
 * mesh built from a raw geometry multiplies its albedo by zero and renders as a
 * pure black silhouette in a perfectly well-lit room. The suit of armour in the
 * medieval store had a lit box torso and black cylinder limbs in the same
 * object, and the symptom is indistinguishable from a lighting bug.
 *
 * Rather than remember to wrap every `new CylinderGeometry` at ~85 call sites
 * and every one written from here on, the whole scene gets swept once at build.
 * White is the identity value, so this can never darken anything that already
 * did the right thing.
 */
export function ensureVertexColors(root: Object3D): void {
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    if (!mats.some((m) => (m as { vertexColors?: boolean })?.vertexColors)) return;
    if (mesh.geometry.getAttribute('color')) return;
    flatVertexColor(mesh.geometry);
  });
}
